import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SaveIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import * as React from "react";
import { NumericFormat } from "react-number-format";
import useNotify from "../../unit/useNotify";
export default function CustomControlMoneyEditTextField({ ...props }) {
  const {
    label,
    name,
    defaultValue,
    value,
    onChange,
    onBlur,
    helperText,
    required,
    prefix,
    readonly,
    editControl,
    min,
    max,
    className,
  } = props;
  const [error, setError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [readOnly, setReadOnly] = React.useState(readonly ? true : false);
  const [values, setValues] = React.useState(defaultValue?.toString() || "");
  const validationTimerRef = React.useRef<number | null>(null);
  const { successNotify, errorNotify, infoNotify } = useNotify();

  const normalizeValue = React.useCallback((rawValue: unknown) => {
    return String(rawValue ?? "")
      .replace(/,/g, "")
      .trim();
  }, []);

  const stringifyValue = React.useCallback((rawValue: unknown) => {
    return rawValue == null ? "" : rawValue.toString();
  }, []);

  const validateAmount = React.useCallback(
    (rawValue: unknown, shouldNotify = false) => {
      const normalizedValue = normalizeValue(rawValue);

      if (normalizedValue === "") {
        if (required) {
          setError(true);
          if (helperText?.toString() == "no") {
            setErrorMessage("This field is required");
          } else {
            setErrorMessage(helperText?.toString() || "This field is required");
          }
          return false;
        }

        setError(false);
        setErrorMessage("");
        return true;
      }

      const amountNumber = parseFloat(normalizedValue);
      const maxNumber = parseFloat(max);
      const minNumber = parseFloat(min);

      if (!Number.isNaN(maxNumber) && amountNumber > maxNumber) {
        setError(true);
        setErrorMessage("The amount cannot exceed the maximum amount");
        if (shouldNotify) {
          errorNotify("The amount cannot exceed the maximum amount");
        }
        return false;
      }

      if (!Number.isNaN(minNumber) && amountNumber < minNumber) {
        setError(true);
        setErrorMessage("The amount cannot be less than the minimum amount");
        if (shouldNotify) {
          errorNotify("The amount cannot be less than the minimum amount");
        }
        return false;
      }

      setError(false);
      setErrorMessage("");
      return true;
    },
    [errorNotify, helperText, max, min, normalizeValue, required]
  );

  const handleEdit = () => {
    setReadOnly(false);
  };
  const handleSaveEdit = () => {
    setReadOnly(true);
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (validationTimerRef.current !== null) {
      window.clearTimeout(validationTimerRef.current);
    }

    setValues(event.target.value);
    validationTimerRef.current = window.setTimeout(() => {
      validateAmount(event.target.value, false);
    }, 800);

    if (onChange) {
      onChange(event);
    }
  };

  React.useEffect(() => {
    if (value !== undefined) {
      setValues(stringifyValue(value));
      return;
    }

    setValues(stringifyValue(defaultValue));
  }, [defaultValue, stringifyValue, value]);

  React.useEffect(() => {
    return () => {
      if (validationTimerRef.current !== null) {
        window.clearTimeout(validationTimerRef.current);
      }
    };
  }, []);

  const handleBlur = (event: any) => {
    if (validationTimerRef.current !== null) {
      window.clearTimeout(validationTimerRef.current);
      validationTimerRef.current = null;
    }

    validateAmount(values, false);

    if (onBlur) {
      onBlur(event);
    }
  };
  let helperTextValue = helperText?.toString() || "This field is required";
  if (helperText === "no") {
    helperTextValue = "";
  }
  return (
    <Stack direction="row" spacing={2}>
      <NumericFormat
        name={name}
        id={name}
        value={values}
        onChange={handleChange}
        onBlur={handleBlur}
        error={error}
        helperText={error ? errorMessage || helperTextValue : null}
        customInput={TextField}
        thousandSeparator
        valueIsNumericString
        prefix={prefix ? "$" : ""}
        variant="outlined"
        label={label}
        className={className}
        sx={{
          // "& input[readonly]": { background: "#e5e5e5" },
          "& .MuiInputBase-readOnly": { background: "#e5e5e5" },
          "& input": { pl: "0px", pt: "8px" },
        }}
        InputProps={
          editControl == false
            ? {
                readOnly: true,
                startAdornment: <AttachMoneyIcon sx={{ fontSize: "16px" }} />,
                endAdornment: <></>,
              }
            : {
                readOnly: readOnly,
                startAdornment: <AttachMoneyIcon sx={{ fontSize: "16px" }} />,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={readOnly ? handleEdit : handleSaveEdit}
                    >
                      {readOnly ? (
                        <EditIcon sx={{ fontSize: "16px", color: "#1976d2" }} />
                      ) : (
                        <SaveIcon sx={{ fontSize: "16px", color: "#1976d2" }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }
        }
      />
    </Stack>
  );
}
